// API utilities for notifications
import axios from 'axios';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:27000";

export interface SendNotificationPayload {
  recipients: {
    appChannel: string;
    userEmail?: string;
    deviceTokens?: string[];
    topic?: string;
  };
  notificationType: 'Booking' | 'Promotion' | 'System';
  message: {
    title: {
      en: string;
      vi: string;
    };
    text: {
      en: string;
      vi: string;
    };
  };
  imageAttach?: string;
  data?: Record<string, any>;
  notificationId?: string;
}

export interface DirectSendNotificationPayload {
  notificationId: string;
  deviceTokens: string[];
  topic?: string;
  data?: {
    bookingId?: string;
    deepLink?: string;
    title?: string;
    body?: string;
    type?: string;
    [key: string]: any;
  };
}

export interface MarkAsReadPayload {
  notificationIds: string[];
}

export const notificationApi = {
  // Get all notifications for the user
  getNotifications: async (userId?: string, page: number = 1, limit: number = 10) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // If userId is provided, get notifications for that user
      // Otherwise get notifications for the current user
      const url = userId 
        ? `${API_BASE_URL}/notifications/user/${userId}?page=${page}&limit=${limit}`
        : `${API_BASE_URL}/notifications?page=${page}&limit=${limit}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },
  
  // Register device token with the backend for FCM
  registerDevice: async (fcmToken: string, userId?: string, topics?: string[]) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const payload = {
        token: fcmToken,
        userId,
        topics
      };

      const response = await axios.post(
        `${API_BASE_URL}/notifications/register-device`, 
        payload, 
        { headers }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error registering FCM token:', error);
      throw error;
    }
  },
  
  // Send a notification through the backend API
  sendNotification: async (payload: SendNotificationPayload) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // If no notificationId is provided, generate one
      if (!payload.notificationId) {
        payload.notificationId = `NOTI-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }
      
      const response = await axios.post(`${API_BASE_URL}/notifications`, payload, {
        headers
      });
      
      return response.data;
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  },
  
  // Send a direct notification to device tokens
  sendDirectNotification: async (payload: DirectSendNotificationPayload) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API_BASE_URL}/notifications/send`, payload, {
        headers
      });
      
      return response.data;
    } catch (error) {
      console.error('Error sending direct notification:', error);
      throw error;
    }
  },
  
  // Mark notifications as read
  markAsRead: async (notificationIds: string[]) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.put(
        `${API_BASE_URL}/notifications/mark-as-read`, 
        { notificationIds },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      throw error;
    }
  },
  
  // Get notification history for a specific notification
  getNotificationHistory: async (notificationId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(
        `${API_BASE_URL}/notifications/history/${notificationId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching notification history:', error);
      throw error;
    }
  }
};

export default notificationApi;